import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, ThumbsUp, ThumbsDown, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface Idea {
  title: string;
  description: string;
  initial_goal: string;
  category: string;
  tags: string[];
  difficulty: string;
  why: string;
  problem?: string;
  approach?: string;
  example_output?: string;
  effort_note?: string;
  time_saved?: string;
  who_uses_this?: string;
}

interface IdeaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  idea: Idea | null;
  feedback: 'up' | 'down' | undefined;
  onFeedback: (idea: Idea, feedback: 'up' | 'down') => void;
  onBuild: (idea: Idea) => void;
}

const DIFFICULTY_LABELS: Record<string, { label: string; className: string }> = {
  beginner: { label: "Easy Win", className: "bg-green-500/15 text-green-400 border-green-500/30" },
  intermediate: { label: "Intermediate", className: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  advanced: { label: "Advanced", className: "bg-red-500/15 text-red-400 border-red-500/30" },
};


export function IdeaDetailModal({ isOpen, onClose, idea, feedback, onFeedback, onBuild }: IdeaDetailModalProps) {
  const isMobile = useIsMobile();

  if (!idea) return null;

  const diffConfig = DIFFICULTY_LABELS[idea.difficulty];
  const hasDetails = idea.problem || idea.approach;

  const desktopClasses = "max-w-lg max-h-[80vh] min-h-0 p-0 gap-0 !flex !flex-col";
  const mobileClasses =
    "fixed inset-0 w-full h-full max-w-none max-h-none rounded-none border-0 p-0 gap-0 translate-x-0 translate-y-0 top-0 left-0 !flex !flex-col";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${isMobile ? mobileClasses : desktopClasses} [&>button.absolute]:hidden`}>
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-border sticky top-0 bg-background z-10 shrink-0">
          {isMobile ? (
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          ) : null}
          <span className="font-semibold flex-1">{isMobile ? "Idea Details" : idea.title}</span>
          {!isMobile && (
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 shrink-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Scrollable body */}
        <div className={isMobile ? "flex-1 overflow-y-auto min-h-0 px-4 pt-4 pb-8" : "flex-1 overflow-y-auto min-h-0 px-6 py-6"}>
          <div className="space-y-4">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {diffConfig && (
                <Badge variant="secondary" className={`text-xs ${diffConfig.className}`}>
                  {diffConfig.label}
                </Badge>
              )}
              {idea.time_saved && (
                <span className="text-xs text-muted-foreground">⏱ {idea.time_saved}</span>
              )}
            </div>

            {/* Title & Description */}
            <div>
              <h2 className="text-lg font-semibold text-foreground">{idea.title}</h2>
              <p className="text-[15px] text-foreground/80 mt-1">{idea.description}</p>
            </div>

            {/* Detail sections */}
            {hasDetails && (
              <div className="space-y-4 pt-3 border-t border-border/50">
                {idea.problem && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Problem</p>
                    <p className="text-[14px] text-foreground/80">{idea.problem}</p>
                  </div>
                )}
                {idea.approach && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Solution</p>
                    <p className="text-[14px] text-foreground/80">{idea.approach}</p>
                  </div>
                )}
              </div>
            )}
            {idea.who_uses_this && (
              <div className="pt-3 border-t border-border/50">
                <p className="text-[13px] text-muted-foreground">
                  <span className="font-semibold">Great for:</span> {idea.who_uses_this}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer - feedback + build */}
        <div className="px-6 py-4 border-t border-border/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className={`h-8 w-8 p-0 ${feedback === "down" ? "text-red-400" : "text-muted-foreground/50 hover:text-red-400"}`}
              onClick={() => onFeedback(idea, "down")}
            >
              <ThumbsDown className={`h-4 w-4 ${feedback === "down" ? "fill-current" : ""}`} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={`h-8 w-8 p-0 ${feedback === "up" ? "text-green-400" : "text-muted-foreground/50 hover:text-green-400"}`}
              onClick={() => onFeedback(idea, "up")}
            >
              <ThumbsUp className={`h-4 w-4 ${feedback === "up" ? "fill-current" : ""}`} />
            </Button>
          </div>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => {
              onBuild(idea);
              onClose();
            }}
          >
            Build <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
